import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { useGetChargePointsPaginatedFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-charge-points-paginated.form-validation';
import { GetChargePointsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-charge-points-paginated.model';
import { DateRangeFilterModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/core/src/models/inputs/date-range-filter.model';
import { Controller, useFormContext } from 'react-hook-form';

import { ChargePointHasLocationFilterController } from '@app/feature/charge-point/charge-point-has-location-filter/charge-point-has-location-filter.controller';
import { ChargePointHasSubscriptionFilterController } from '@app/feature/charge-point/charge-point-has-subscription-filter/charge-point-has-subscription-filter.controller';
import { ChargePointHasTariffFilterController } from '@app/feature/charge-point/charge-point-has-tariff-filter/charge-point-has-tariff-filter.controller';
import { ChargePointIsMaintenanceFilterController } from '@app/feature/charge-point/charge-point-is-maintenance-filter/charge-point-is-maintenance-filter.controller';
import { ChargePointIsOnlineFilterController } from '@app/feature/charge-point/charge-point-is-online-filter/charge-point-is-online-filter.controller';
import { ChargePointListLastMessageReceivedDateFilterController } from '@app/feature/charge-point/charge-point-list-last-message-received-date-filter/charge-point-list-last-message-received-date-filter.controller';
import { CustomerFilterController } from '@app/feature/customer/customer-filter/customer-filter.controller';
import { LocationFilterController } from '@app/feature/location/location-filter/location-filter.controller';
import { NetworkFilterController } from '@app/feature/network/network-filter/network-filter.controller';
import { TariffFilterController } from '@app/feature/tariff/tariff-filter/tariff-filter.controller';
import { toClearableFieldValue } from '@shared/form/to-clearable-field-value.util';

const ChargePointListFilterFormStep1IsOnlineFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    // const { t } = useTranslation();
    const { control } = useFormContext<GetChargePointsPaginatedModel>();
    const { isOnline } = useGetChargePointsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="isOnline"
          control={control}
          rules={isOnline}
          render={({ field /* , fieldState: { error } */ }) => {
            return (
              <ChargePointIsOnlineFilterController
                selectedItem={field.value}
                onChange={(value) =>
                  field.onChange(toClearableFieldValue(value))
                }
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChargePointListFilterFormStep1IsMaintenanceFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    // const { t } = useTranslation();
    const { control } = useFormContext<GetChargePointsPaginatedModel>();
    const { isMaintenance } = useGetChargePointsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="isMaintenance"
          control={control}
          rules={isMaintenance}
          render={({ field /* , fieldState: { error } */ }) => {
            return (
              <ChargePointIsMaintenanceFilterController
                selectedItem={field.value}
                onChange={(value) =>
                  field.onChange(toClearableFieldValue(value))
                }
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChargePointListFilterFormStep1CustomerIdFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    // const { t } = useTranslation();
    const { control } = useFormContext<GetChargePointsPaginatedModel>();
    const { customerId } = useGetChargePointsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="customerId"
          control={control}
          rules={customerId}
          render={({ field /* , fieldState: { error } */ }) => {
            const values = [];

            if (Array.isArray(field.value)) {
              values.push(...field.value);
            } else if (field.value) {
              values.push(field.value);
            }

            return (
              <CustomerFilterController
                selectedItemIds={values}
                onChange={(value) => field.onChange(value)}
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChargePointListFilterFormStep1HasTariffFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    // const { t } = useTranslation();
    const { control } = useFormContext<GetChargePointsPaginatedModel>();
    const { hasTariff } = useGetChargePointsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="hasTariff"
          control={control}
          rules={hasTariff}
          render={({ field /* , fieldState: { error } */ }) => {
            return (
              <ChargePointHasTariffFilterController
                selectedItem={field.value}
                onChange={(value) =>
                  field.onChange(toClearableFieldValue(value))
                }
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChargePointListFilterFormStep1HasLocationFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    // const { t } = useTranslation();
    const { control } = useFormContext<GetChargePointsPaginatedModel>();
    const { hasLocation } = useGetChargePointsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="hasLocation"
          control={control}
          rules={hasLocation}
          render={({ field /* , fieldState: { error } */ }) => {
            return (
              <ChargePointHasLocationFilterController
                selectedItem={field.value}
                onChange={(value) =>
                  field.onChange(toClearableFieldValue(value))
                }
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChargePointListFilterFormStep1HasSubscriptionFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    // const { t } = useTranslation();
    const { control } = useFormContext<GetChargePointsPaginatedModel>();
    const { hasSubscription } = useGetChargePointsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="hasSubscription"
          control={control}
          rules={hasSubscription}
          render={({ field /* , fieldState: { error } */ }) => {
            return (
              <ChargePointHasSubscriptionFilterController
                selectedItem={field.value}
                onChange={(value) =>
                  field.onChange(toClearableFieldValue(value))
                }
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChargePointListFilterFormStep1LastMessageReceivedDateFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { control } = useFormContext<GetChargePointsPaginatedModel>();
    const { lastMessageReceivedDate } =
      useGetChargePointsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="lastMessageReceivedDate"
          control={control}
          rules={lastMessageReceivedDate}
          render={({ field }) => {
            // The SDK field also allows an exact-match scalar date, but this
            // filter UI only ever reads/writes the min/max range shape.
            const value = field.value as DateRangeFilterModel | undefined;

            return (
              <ChargePointListLastMessageReceivedDateFilterController
                value={value}
                onChange={field.onChange}
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChargePointListFilterFormStep1LocationIdFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    // const { t } = useTranslation();
    const { control } = useFormContext<GetChargePointsPaginatedModel>();
    const { locationId } = useGetChargePointsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="locationId"
          control={control}
          rules={locationId}
          render={({ field /* , fieldState: { error } */ }) => {
            const values = [];

            if (Array.isArray(field.value)) {
              values.push(...field.value);
            } else if (field.value) {
              values.push(field.value);
            }

            return (
              <LocationFilterController
                selectedItemIds={values}
                onChange={(value) => field.onChange(value)}
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChargePointListFilterFormStep1NetworkIdFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    // const { t } = useTranslation();
    const { control } = useFormContext<GetChargePointsPaginatedModel>();
    const { networkId } = useGetChargePointsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Controller
          name="networkId"
          control={control}
          rules={networkId}
          render={({ field /* , fieldState: { error } */ }) => {
            const values = [];

            if (Array.isArray(field.value)) {
              values.push(...field.value);
            } else if (field.value) {
              values.push(field.value);
            }

            return (
              <NetworkFilterController
                selectedItemIds={values}
                onChange={(value) => field.onChange(value)}
              />
            );
          }}
        />
        <Divider />
      </>
    );
  };

const ChargePointListFilterFormStep1TariffIdFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    // const { t } = useTranslation();
    const { control } = useFormContext<GetChargePointsPaginatedModel>();
    const { tariffId } = useGetChargePointsPaginatedFormValidation();

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <Controller
        name="tariffId"
        control={control}
        rules={tariffId}
        render={({ field /* , fieldState: { error } */ }) => {
          const values = [];

          if (Array.isArray(field.value)) {
            values.push(...field.value);
          } else if (field.value) {
            values.push(field.value);
          }

          return (
            <TariffFilterController
              selectedItemIds={values}
              onChange={(value) => field.onChange(value)}
            />
          );
        }}
      />
    );
  };

export const ChargePointListFilterFormStep1Controller =
  (): React.JSX.Element => {
    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <Stack
        direction="row"
        justifyContent="stretch"
        alignItems="stretch"
        flex={1}
        gap={GapEnum.M}>
        <Stack direction="column" flex={1} alignItems="stretch" gap={GapEnum.M}>
          <ChargePointListFilterFormStep1IsOnlineFragment />
          <ChargePointListFilterFormStep1IsMaintenanceFragment />
          <ChargePointListFilterFormStep1CustomerIdFragment />
          <ChargePointListFilterFormStep1HasTariffFragment />
          <ChargePointListFilterFormStep1HasLocationFragment />
          <ChargePointListFilterFormStep1HasSubscriptionFragment />
          <ChargePointListFilterFormStep1LastMessageReceivedDateFragment />
          <ChargePointListFilterFormStep1LocationIdFragment />
          <ChargePointListFilterFormStep1NetworkIdFragment />
          <ChargePointListFilterFormStep1TariffIdFragment />
        </Stack>
      </Stack>
    );
  };
